import DOMPurify from "dompurify";

/** Viewer chrome that older converters (pdf2htmlEX) left in the stored body. */
const STRIPPED_FROM_BODY = "style, #sidebar, .loading-indicator";

const STYLE_BLOCK_SEPARATOR = "\n\n/* --- next style block --- */\n\n";

export interface PreparedStoredDocument {
  /** Sanitized body markup, ready for DocumentRenderer (`rawHtmlIsSanitized`). */
  rawHTML: string;
  /** The document's own stylesheets, in document order, unscoped. */
  rawCSS: string;
}

/**
 * Turn a stored workspace document into sanitized body markup plus its CSS.
 *
 * One inert DOMParser pass replaces the two `innerHTML` parses into live-DOM
 * elements used before (LAR-487). On a 16 MB OCR document that saves ~0.7 s,
 * and an inert document never runs inline `onerror`/`onload` handlers or
 * fetches images before DOMPurify has seen the markup.
 *
 * DOMPurify runs with its default config here, whose URI allowlist the
 * renderer's SANITIZE_HTML_OPTIONS shares (plus `blob:`), so `javascript:`
 * hrefs are stripped either way. That is why the renderer may skip its own pass
 * for this output.
 */
export function prepareStoredDocumentHtml(
  html: string
): PreparedStoredDocument {
  if (!html) return { rawHTML: "", rawCSS: "" };

  const doc = new DOMParser().parseFromString(html, "text/html");

  const rawCSS = Array.from(
    doc.querySelectorAll("style"),
    (el) => el.textContent || ""
  ).join(STYLE_BLOCK_SEPARATOR);

  doc.body.querySelectorAll(STRIPPED_FROM_BODY).forEach((el) => el.remove());

  const rawHTML = DOMPurify.sanitize(doc.body.innerHTML, {
    ALLOW_UNKNOWN_PROTOCOLS: false,
    WHOLE_DOCUMENT: false,
  });

  return { rawHTML, rawCSS };
}
